import { useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import {
  createGenericLayer,
  deleteGenericLayer,
  renameGenericLayer,
  updateGenericLayerStyle,
  type GenericLayer,
  type GenericLayerStyle,
} from "../../model/genericLayer.ts";
import { loadGisFiles } from "../gisUpload.ts";
import { ConfirmDeleteDialog } from "../ConfirmDeleteDialog.tsx";
import { FileButton } from "../FileButton.tsx";
import { TrashIcon } from "../TrashIcon.tsx";

const RADIUS_RANGE = { min: 1, max: 30 };
const WEIGHT_RANGE = { min: 1, max: 15 };
const FILL_OPACITY_RANGE = { min: 0, max: 1 };

/**
 * The `min`/`max` attributes below only style the (now-removed, see
 * styles.css) native spin buttons — they don't stop the user from typing an
 * out-of-range or empty value directly, and `updateGenericLayerStyle`
 * stores whatever it's given with no validation of its own. Clamp here
 * instead, falling back to the field's current value for anything that
 * doesn't even parse (empty string, a stray "-"), so a bad keystroke can't
 * leave the style with NaN or a value Leaflet would choke on (e.g. a
 * negative radius).
 */
function clampedNumber(raw: string, range: { min: number; max: number }, current: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return current;
  return Math.min(range.max, Math.max(range.min, n));
}

/**
 * "Layers" editor catalog: purely visual reference layers (points/lines/
 * polygons, no domain meaning — see genericLayer.ts's own header comment).
 * Upload/list/rename/delete, plus a basic style control per row (color
 * always; weight for lines/polygons, radius for points, fill opacity for
 * polygons — whichever apply to that row's own `geometryKind`, see
 * `StyleControls` below). Shown on the map itself via SubBasinMap.tsx's
 * own `LayersControl.Overlay`, with visibility synced to
 * `GenericLayer.visible` (GenericLayersSync, SubBasinMap.tsx).
 */
function StyleControls({
  layer,
  onChange,
}: {
  layer: GenericLayer;
  onChange: (style: Partial<GenericLayerStyle>) => void;
}) {
  const t = useT();
  return (
    <div className="generic-layer-style">
      <input
        type="color"
        title={t("genericLayersPanel.color")}
        value={layer.style.color}
        onChange={(e) => onChange({ color: e.target.value })}
      />
      {layer.geometryKind === "point" && (
        <label className="generic-layer-style-field">
          <span>{t("genericLayersPanel.radius")}</span>
          <input
            type="number"
            min={RADIUS_RANGE.min}
            max={RADIUS_RANGE.max}
            value={layer.style.radius}
            onChange={(e) =>
              onChange({ radius: clampedNumber(e.target.value, RADIUS_RANGE, layer.style.radius) })
            }
          />
        </label>
      )}
      {(layer.geometryKind === "line" || layer.geometryKind === "polygon") && (
        <label className="generic-layer-style-field">
          <span>{t("genericLayersPanel.weight")}</span>
          <input
            type="number"
            min={WEIGHT_RANGE.min}
            max={WEIGHT_RANGE.max}
            value={layer.style.weight}
            onChange={(e) =>
              onChange({ weight: clampedNumber(e.target.value, WEIGHT_RANGE, layer.style.weight) })
            }
          />
        </label>
      )}
      {layer.geometryKind === "polygon" && (
        <label className="generic-layer-style-field">
          <span>{t("genericLayersPanel.fillOpacity")}</span>
          <input
            type="number"
            min={FILL_OPACITY_RANGE.min}
            max={FILL_OPACITY_RANGE.max}
            step={0.1}
            value={layer.style.fillOpacity}
            onChange={(e) =>
              onChange({
                fillOpacity: clampedNumber(
                  e.target.value,
                  FILL_OPACITY_RANGE,
                  layer.style.fillOpacity
                ),
              })
            }
          />
        </label>
      )}
    </div>
  );
}
export function GenericLayersPanel({
  layers,
  onChange,
}: {
  layers: GenericLayer[];
  onChange: (layers: GenericLayer[]) => void;
}) {
  const t = useT();
  const [pendingGeojson, setPendingGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [pendingName, setPendingName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function onFiles(files: FileList | null) {
    if (!files) return;
    // Captured synchronously, before any `await` below: FileButton.tsx
    // resets the `<input>`'s value right after calling this handler, which
    // (since `files` is a live FileList backed by that same input) clears
    // it out from under an `await`ed read — the File object itself, once
    // grabbed here, stays valid independently of the input's later reset.
    const firstFile = files[0];
    setError(null);
    setNotice(null);
    try {
      const { geojson, notice: loadedNotice } = await loadGisFiles(files, t);
      setPendingGeojson(geojson);
      setNotice(loadedNotice);
      setPendingName(firstFile ? firstFile.name.replace(/\.[^.]+$/, "") : "");
    } catch (err) {
      // Same reasoning as GisLayerPanel.tsx's own catch — see its comment.
      console.error("Could not load GIS files:", err);
      setError(translateError(err, t));
      setPendingGeojson(null);
    }
  }

  function confirmAdd() {
    if (!pendingGeojson) return;
    try {
      onChange(createGenericLayer(layers, pendingName, pendingGeojson));
      setPendingGeojson(null);
      setPendingName("");
      setError(null);
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  function rename(id: string, name: string) {
    if (!name.trim()) return;
    try {
      onChange(renameGenericLayer(layers, id, name));
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  const deletingLayer = layers.find((l) => l.id === deletingId);

  return (
    <div>
      <h3>{t("panels.layers")}</h3>
      <p>{t("genericLayersPanel.intro")}</p>

      <FileButton multiple accept=".shp,.dbf,.prj,.shx,.cpg,.geojson,.json" onFiles={onFiles} />
      {error && <p className="error">{error}</p>}
      {notice && <p className="engine-notice">{notice}</p>}

      {pendingGeojson && (
        <div className="step-form">
          <input
            placeholder={t("common.name")}
            value={pendingName}
            onChange={(e) => setPendingName(e.target.value)}
          />
          <button className="button button-primary" onClick={confirmAdd}>
            {t("genericLayersPanel.addButton")}
          </button>
        </div>
      )}

      {layers.length > 0 && (
        <div className="generic-layers-list catalog-table-scroll">
          {layers.map((l) => (
            <div key={l.id} className="generic-layer-row">
              <div className="generic-layer-row-top">
                <input defaultValue={l.name} onBlur={(e) => rename(l.id, e.target.value)} />
                <button
                  className="button-icon button-danger"
                  title={t("genericLayersPanel.deletePermanent")}
                  onClick={() => setDeletingId(l.id)}
                >
                  <TrashIcon />
                </button>
              </div>
              <div className="generic-layer-row-bottom">
                <span className="generic-layer-kind">
                  {t(`genericLayersPanel.kind.${l.geometryKind}`)}
                </span>
                <StyleControls
                  layer={l}
                  onChange={(style) => onChange(updateGenericLayerStyle(layers, l.id, style))}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {deletingLayer && (
        <ConfirmDeleteDialog
          title={t("genericLayersPanel.deleteDialogTitle")}
          name={deletingLayer.name}
          message={t("genericLayersPanel.deleteMessage", { name: deletingLayer.name })}
          onConfirm={() => onChange(deleteGenericLayer(layers, deletingLayer.id))}
          onClose={() => setDeletingId(null)}
        />
      )}
    </div>
  );
}
